import {isFilesEnabled} from 'auth/feature-enablement/FeatureStatus';
import BenchlingConfig from 'BenchlingConfig';
import * as FileBrowserConstants from 'constants/FileBrowserConstants';
import {FolderSection, StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import * as NavConstants from 'constants/NavConstants';
import {getSearchableRegistrySupportedOligoAwareTypes} from 'constants/RegistryConstants';
import {ProjectPreferences} from 'nav/sidepanel/listing/ProjectListingConstants';
import {getSelectedPageSize} from 'nav/sidepanel/PageSizeUtil';
import preferences from 'Preferences';
import * as FilterUtil from 'search/FilterUtil';
import {
  Filter,
  FilterKey,
  keyToEmptyFilter,
  Operator,
  SearchParams,
  SingleValueFilter,
} from 'search/SearchConstants';
import * as SearchPreferences from 'search/SearchPreferences';
import * as SearchUtil from 'search/SearchUtil';
import isModelTypeEnabledForListing from 'util/isModelTypeEnabledForListing';

export function getDefaultProjectSearchParams(): SearchParams {
  return {
    offset: 0,
    limit: getSelectedPageSize(),
    filters: getInitialProjectFilters(),
    query: '',
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.PROJECTS),
  };
}

function getInitialProjectFilters(): Array<Filter> {
  const filters: Array<Filter> = [];

  const types = [ModelTypeConstants.SOURCE];
  if (BenchlingConfig.PERMISSIONS_ENABLE_GRANULAR_FOLDER_PERMISSIONS) {
    types.push(ModelTypeConstants.FOLDER);
    filters.push({
      ...keyToEmptyFilter(FilterKey.TOP_LEVEL_PERMISSIONS),
      value: 'true',
    });
  }

  filters.push({
    ...keyToEmptyFilter(FilterKey.TYPES),
    value: types,
  });
  const sharedWithFilterValue = preferences.get(ProjectPreferences.LAST_SELECTED_PROJECT_FILTER);
  if (sharedWithFilterValue) {
    filters.push({
      key: FilterKey.SHARED_WITH,
      operator: Operator.IS,
      value: sharedWithFilterValue ?? null,
    });
  }
  const attributeFilterValue = preferences.get(ProjectPreferences.LAST_SELECTED_PROJECT_ATTRIBUTE_FILTER);
  filters.push({
    key: FilterKey.PROJECT_ATTRIBUTE,
    operator: Operator.IS,
    value: attributeFilterValue ?? null,
  });
  return filters;
}

export const INVENTORY_FILTER: Filter = {
  key: FilterKey.FOLDER_SECTION,
  operator: Operator.IS,
  value: FileBrowserConstants.INVENTORY_SECTION,
};

export const NOTEBOOK_FILTER: Filter = {
  key: FilterKey.FOLDER_SECTION,
  operator: Operator.IS,
  value: FileBrowserConstants.NOTEBOOK_SECTION,
};

export const ALL_FILTER: Filter = {
  key: FilterKey.FOLDER_SECTION,
  operator: Operator.IS,
  value: FileBrowserConstants.ALL_SECTION,
};

export const PRESERVED_FILTER_KEYS = [FilterKey.FOLDER_SECTION, FilterKey.FOLDER, FilterKey.ANCESTOR_FOLDER];

export function getDefaultProjectItemsSearchParams(folder: StringIDFolder): SearchParams {
  const defaultSection = FolderSection.ALL;
  const sectionFilter = ALL_FILTER;

  return {
    filters: [
      sectionFilter,
      {
        ...keyToEmptyFilter(FilterKey.FOLDER),
        // If folder.api_identifier is not present, folder.id is likely the API ID.
        value: folder.api_identifier || folder.id,
      },
      {
        ...keyToEmptyFilter(FilterKey.TYPES),
        value: getDefaultTypesForSection(defaultSection),
      },
    ],
    limit: getSelectedPageSize(),
    offset: 0,
    query: '',
    sorts: SearchPreferences.getAppliedSortsFromUserPreference(NavConstants.Section.PROJECT_ITEMS),
  };
}

const NOTEBOOK_DEFAULT_TYPES: Array<ModelTypeConstants> = [
  ModelTypeConstants.FOLDER,
  ModelTypeConstants.ENTRY,
  ModelTypeConstants.PROCESS,
  ModelTypeConstants.PROTOCOL,
];

const INVENTORY_DEFAULT_OLIGO_AWARE_TYPES = [
  ModelTypeConstants.FOLDER,
  ModelTypeConstants.BATCH,
  ModelTypeConstants.BULK_ASSEMBLY,
  ...getSearchableRegistrySupportedOligoAwareTypes(),
];

function getAllDefaultTypesForSection(section: string): Array<string> {
  const inventoryTypes = INVENTORY_DEFAULT_OLIGO_AWARE_TYPES;
  switch (section) {
    case FileBrowserConstants.ALL_SECTION:
      return Array.from(
        new Set([
          ...NOTEBOOK_DEFAULT_TYPES,
          ...inventoryTypes,
          ...[ModelTypeConstants.ANALYSIS],
          ...(BenchlingConfig.REQUESTS_V2_ENABLE ? [ModelTypeConstants.REQUEST_V2_SUBMISSION] : []),
          ...(BenchlingConfig.APP_CORE_ENABLE_FAKE_ITEM_TYPE ? [ModelTypeConstants.JELLYFISH] : []),
          ...(BenchlingConfig.ENABLE_ANALYSIS_TOOL_WITHOUT_INSIGHTS ? [ModelTypeConstants.DATASET] : []),
          ...(BenchlingConfig.PROCEDURES_ENABLE ? [ModelTypeConstants.WORKSHEET] : []),
          ...(isFilesEnabled() ? [ModelTypeConstants.FILE] : []),
          ...(BenchlingConfig.PROCEDURES_ENABLE ? [ModelTypeConstants.RECIPE_PROCESS] : []),
          ...(BenchlingConfig.PROCEDURES_ENABLE_ASSAYS ? [ModelTypeConstants.ASSAY_PROCESS] : []),
          ...(BenchlingConfig.STUDIES_ENABLE ? [ModelTypeConstants.STUDY] : []),
        ])
      );
    case FileBrowserConstants.NOTEBOOK_SECTION:
      return NOTEBOOK_DEFAULT_TYPES;
    case FileBrowserConstants.INVENTORY_SECTION:
      return inventoryTypes;
    default:
      throw new Error(`Unexpected section type in getAllDefaultTypesForSection: ${section}`);
  }
}

export function getDefaultTypesForSection(section: string): Array<string> {
  return getAllDefaultTypesForSection(section).filter((type) => isModelTypeEnabledForListing(type));
}

/**
 * Apply the correct folder filter based on the search params, and return the resolved search params.
 */
export function resolveFolderFilter(searchParams: SearchParams): SearchParams {
  const shouldIncludeSubfolders = SearchUtil.hasQueryOrNonDefaultFilters(
    searchParams,
    PRESERVED_FILTER_KEYS,
    getDefaultTypesForSection(
      FilterUtil.getFilterValueByKey(searchParams.filters, FilterKey.FOLDER_SECTION) as string
    )
  );
  const filtersWithoutFolderFilter = searchParams.filters.filter(
    (filter) => ![FilterKey.FOLDER, FilterKey.ANCESTOR_FOLDER].includes(filter.key)
  );
  const newFilters = FilterUtil.replaceByKey(filtersWithoutFolderFilter, {
    key: shouldIncludeSubfolders ? FilterKey.ANCESTOR_FOLDER : FilterKey.FOLDER,
    operator: Operator.IS,
    value:
      FilterUtil.getFilterValueByKey(searchParams.filters, FilterKey.FOLDER) ||
      FilterUtil.getFilterValueByKey(searchParams.filters, FilterKey.ANCESTOR_FOLDER),
  } as SingleValueFilter);
  return {...searchParams, filters: newFilters};
}
